// pages/formularios/RespostasFormularios.js — aba "Respostas".
//
// Out/26, Formulários PR 2. Um formulário por vez: quantos foram enviados
// pelo chat, quantos responderam, e cada resposta com o que foi pro CRM —
// o que entrou, o que ficou como estava porque o CRM já tinha outro valor,
// e a pasta. Testes do rascunho não contam aqui (vivem no construtor).
//
// PR 3: os DISPAROS — cada envio em massa e cada agendamento do template
// com o botão do formulário vira um cartão com enviados, respondidos e a
// taxa; o que saiu um a um pelo chat é um cartão só. Escolher um cartão
// filtra as respostas.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { FiCalendar, FiMessageCircle, FiSend, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../services/Api';
import RespostasLista from './RespostasLista';
import { quando } from './ListaFormularios';
import { SYNE, ajuda, botaoTexto, campo, menuPapel, selo } from './estilo';

const CORES = {
  texto: 'var(--fm-ink)', texto2: 'var(--fm-label)', texto3: 'var(--fm-cap)', borda: 'var(--fm-bdr)',
  fundo: 'var(--fm-bg)', verde: 'var(--fm-green-t)', ambar: 'var(--fm-amber-t)',
};

function Numero({ valor, rotulo }) {
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography sx={{ fontFamily: "'Outfit', system-ui, sans-serif", fontWeight: 700, fontSize: '1.6rem', color: 'var(--fm-title)', lineHeight: 1 }}>{valor}</Typography>
      <Typography sx={{ ...ajuda, mt: 0.5 }}>{rotulo}</Typography>
    </Box>
  );
}

const ICONE = { lote: FiSend, agendado: FiCalendar, um_a_um: FiUser, agenda: FiCalendar, fora: FiMessageCircle };

function CartaoDisparo({ d, ativo, onClick }) {
  const Icone = ICONE[d.tipo] || FiSend;
  return (
    <Box
      component="button" type="button" role="radio" aria-checked={ativo} onClick={onClick}
      sx={{
        textAlign: 'left', font: 'inherit', cursor: 'pointer', p: '12px 14px', borderRadius: '12px', minWidth: 0,
        bgcolor: ativo ? 'var(--fm-green-bg)' : 'var(--fm-paper)', color: 'var(--fm-ink)',
        border: ativo ? '2px solid var(--fm-green)' : '1px solid var(--fm-bdr)', m: ativo ? 0 : '1px',
        '&:hover': { borderColor: ativo ? 'var(--fm-green)' : 'var(--fm-bdr-h)' },
        '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--fm-label)' }}>
        <Icone size={13} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--fm-title)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.rotulo}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: "'Outfit', system-ui, sans-serif", fontSize: '1.05rem', fontWeight: 700, color: 'var(--fm-title)', mt: 0.75, lineHeight: 1.1 }}>
        {d.respondidos}<Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'var(--fm-body)' }}>{d.enviados ? ` de ${d.enviados}` : ''} responderam{d.taxa != null ? ` · ${d.taxa}%` : ''}</Box>
      </Typography>
      {(d.template || d.por) && (
        <Typography sx={{ ...ajuda, mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[d.template, d.por && `por ${d.por}`].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Box>
  );
}

export default function RespostasFormularios({ itens, inicial, onMensagem }) {
  const navigate = useNavigate();
  const opcoes = useMemo(() => itens.filter((f) => f.status !== 'DRAFT'), [itens]);
  const [sel, setSel] = useState(() => inicial || opcoes.find((f) => f.respondidos)?.id || opcoes[0]?.id || '');
  const [dados, setDados] = useState(null);
  const [mais, setMais] = useState(false);
  const [disparos, setDisparos] = useState([]);
  const [disparo, setDisparo] = useState('');

  useEffect(() => { if (inicial) setSel(inicial); }, [inicial]);
  useEffect(() => { if (!sel && opcoes[0]) setSel(opcoes[0].id); }, [sel, opcoes]);
  useEffect(() => {
    setDisparo('');
    setDisparos([]);
    if (!sel) return;
    api.get(`/formularios/${sel}/disparos`).then((r) => setDisparos(r.data?.items || [])).catch(() => setDisparos([]));
  }, [sel]);

  const carregar = useCallback(async (pagina = 1) => {
    if (!sel) return;
    try {
      const r = await api.get(`/formularios/${sel}/respostas`, { params: { pagina, disparo: disparo || undefined } });
      setDados((d) => (pagina === 1 ? r.data : { ...r.data, items: [...(d?.items || []), ...r.data.items] }));
    } catch (err) {
      onMensagem?.({ tipo: 'error', texto: err?.response?.data?.detail || 'Não foi possível carregar as respostas.' });
      setDados((d) => d || { items: [], enviados: 0, respondidos: 0, total: 0, pagina: 1, paginas: 1 });
    }
  }, [sel, disparo, onMensagem]);

  useEffect(() => { setDados(null); carregar(1); }, [carregar]);

  if (!opcoes.length) {
    return (
      <Box sx={{ border: '1px dashed var(--fm-bdr-h)', borderRadius: '14px', py: 6, px: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--fm-title)', fontSize: '1.05rem' }}>Nenhum formulário publicado</Typography>
        <Typography sx={{ ...ajuda, fontSize: '0.84rem', mt: 0.75 }}>As respostas aparecem aqui depois que um formulário publicado for enviado pelo chat.</Typography>
      </Box>
    );
  }

  const taxa = dados?.enviados ? Math.round((100 * Math.min(dados.respondidos, dados.enviados)) / dados.enviados) : null;
  // Mais de um grupo = vale mostrar os disparos (um só repete os números do topo).
  const mostrarDisparos = disparos.length > 1 || disparos.some((d) => d.tipo === 'lote' || d.tipo === 'agendado');

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
        <TextField
          select size="small" label="Formulário" value={sel} onChange={(e) => setSel(e.target.value)}
          SelectProps={{ MenuProps: menuPapel }} sx={{ ...campo, minWidth: 260, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        >
          {opcoes.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
        </TextField>
        {dados && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Numero valor={dados.enviados} rotulo="enviados" />
            <Numero valor={dados.respondidos} rotulo={taxa != null ? `responderam (${taxa}%)` : 'responderam'} />
          </Box>
        )}
      </Box>
      {mostrarDisparos && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.8rem', color: 'var(--fm-label)', mb: 1 }}>Por disparo</Typography>
          <Box role="radiogroup" aria-label="Disparos" sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))' }}>
            <CartaoDisparo
              d={{ tipo: 'todos', rotulo: 'Todos os envios', enviados: dados?.enviados || 0, respondidos: dados?.respondidos || 0, taxa }}
              ativo={!disparo} onClick={() => setDisparo('')}
            />
            {disparos.map((d) => (
              <CartaoDisparo key={d.chave} d={d} ativo={disparo === d.chave} onClick={() => setDisparo(d.chave)} />
            ))}
          </Box>
        </Box>
      )}
      {dados?.de_fora > 0 && (
        <Typography sx={{ ...ajuda, mt: -1.5, mb: 2 }}>
          {dados.de_fora === 1 ? '1 resposta veio' : `${dados.de_fora} respostas vieram`} de um envio feito fora da Cetto.
        </Typography>
      )}

      {!dados ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'var(--fm-green)' }} /></Box>
      ) : !dados.items.length ? (
        <Box sx={{ border: '1px dashed var(--fm-bdr-h)', borderRadius: '14px', py: 5, px: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--fm-title)', fontSize: '1rem' }}>
            {disparo ? 'Ninguém respondeu a este disparo ainda' : 'Ninguém respondeu ainda'}
          </Typography>
          <Typography sx={{ ...ajuda, fontSize: '0.84rem', mt: 0.75 }}>
            {disparo
              ? 'As respostas aparecem aqui conforme os clientes tocam no botão do formulário.'
              : 'Envie pelo chat (botão de formulário, no rodapé da conversa) ou num template com o botão deste formulário.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
          {dados.items.map((e) => (
            <Box key={e.id} data-resposta={e.id} sx={{ border: '1px solid var(--fm-bdr)', borderRadius: '14px', bgcolor: 'var(--fm-paper)', p: { xs: 2, sm: 2.25 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: SYNE, fontWeight: 800, fontSize: '0.95rem', color: 'var(--fm-title)', wordBreak: 'break-word' }}>
                    {e.contato_nome || 'Contato sem nome'}
                  </Typography>
                  <Typography sx={{ ...ajuda, mt: 0.3 }}>
                    Respondeu {quando(e.respondido_em)}
                    {e.origem === 'fora' ? ' · enviado fora da Cetto' : e.enviado_por ? ` · enviado por ${e.enviado_por}` : ''}
                  </Typography>
                </Box>
                {e.vezes > 1 && <Box component="span" sx={selo('gray')}>{e.vezes} respostas</Box>}
                {e.device_id && (
                  <Button size="small" startIcon={<FiMessageCircle size={13} />} onClick={() => navigate(`/chat/${e.device_id}/${e.client_id}`)} sx={{ ...botaoTexto, flexShrink: 0 }}>
                    Conversa
                  </Button>
                )}
              </Box>
              <RespostasLista respostas={e.respostas} crm={e.crm} cores={CORES} />
            </Box>
          ))}
        </Box>
      )}

      {dados && dados.pagina < dados.paginas && (
        <Box sx={{ textAlign: 'center', mt: 2.5 }}>
          <Button
            onClick={async () => { setMais(true); await carregar(dados.pagina + 1); setMais(false); }} disabled={mais}
            sx={botaoTexto}
          >
            {mais ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Carregar mais respostas'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
